import { NgModule } from '@angular/core';
import { ExtraOptions, mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { AuthenticatedGuard } from '@kp/auth/guards/authenticated.guard';
import { DeletionGuard } from '@kp/auth/guards/deletion.guard';
import { ExistingAccountGuard } from '@kp/auth/guards/existing-account.guard';
import { LegalDocumentGuard } from '@kp/auth/guards/legal-document.guard';
import { NotAuthenticatedGuard } from '@kp/auth/guards/not-authenticated.guard';
import { ProductGuard } from '@kp/auth/guards/product.guard';
import { SetPasswordGuard } from '@kp/auth/guards/set-password.guard';
import { TokenGuard } from '@kp/auth/guards/token.guard';
import { TagGuard } from '@kp/core/tags/tag.guard';
import { DataLinkGuard } from '@kp/data-link/data-link.guard';
import { EmptyRedirectGuard } from '@kp/empty-redirect.guard';
import { ScreenPrivacyPledgeComponent } from '@kp/onboarding/screen-privacy-pledge/screen-privacy-pledge.component';
import { SingleQuestionComponent } from '@kp/question-set/single-question/single-question.component';
import { ScreenInitialComponent } from '@kp/screen-initial/screen-initial.component';
import { AuthSsoLogoutComponent } from '@kp/screen-login/auth-sso-logout/auth-sso-logout.component';
import { CodeComponent } from '@kp/screen-login/code/code.component';
import { ScreenLogoutComponent } from '@kp/screen-logout/screen-logout.component';
import { ScreenMobileAppInfoComponent } from '@kp/screen-mobile-app-info/screen-mobile-app-info.component';
import { ScreenThankYouComponent } from '@kp/screen-thank-you/screen-thank-you.component';
import { ScreenDeletionConfirmationComponent } from '@kp/settings/screen-deletion-confirmation/screen-deletion-confirmation.component';
import { ScreenDeletionRequestedComponent } from '@kp/settings/screen-deletion-requested/screen-deletion-requested.component';
import { PulseSurveyAvailableGuard } from '@kp/shared/guards/pulse-survey-available.guard';
import { Guards, TrafficCoordinatorGuard } from '@kp/shared/guards/traffic-coordinator.guard';
import { TagKey } from '@ktypes/models';

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
  // enableTracing: true, // <-- debugging purposes only
};

const routes: Routes = [
  {
    path: 'auth/sso',
    component: CodeComponent,
  },
  {
    path: 'auth/sso/logout',
    component: AuthSsoLogoutComponent,
  },
  {
    path: 'feedback',
    loadChildren: () => import('@kp/screen-feedback/screen-feedback.module').then((mod) => mod.ScreenFeedbackModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'initial/:groupCode',
    component: ScreenInitialComponent,
    canActivate: mapToCanActivate([NotAuthenticatedGuard, DataLinkGuard]),
  },
  {
    path: 'token',
    // need empty children array to allow for canActivate;
    // need one of component, children or redirectTo defined per Angular spec
    children: [],
    canActivate: mapToCanActivate([DataLinkGuard]),
  },
  {
    path: 'data-link',
    loadChildren: () => import('@kp/data-link/data-link.module').then((mod) => mod.DataLinkModule),
  },
  {
    path: 'error',
    loadChildren: () => import('@kp/screen-error/screen-error.module').then((mod) => mod.ScreenErrorModule),
    canActivate: mapToCanActivate([AuthenticatedGuard]),
  },
  {
    path: 'discover',
    loadChildren: () => import('@kp/screen-discover/screen-discover.module').then((mod) => mod.ScreenDiscoverModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard]),
  },
  { path: 'explore', redirectTo: 'discover' },
  {
    // using nested routes to ensure LegalDocumentGuard runs before PulseSurveyAvailableGuard is run
    path: 'resources',
    canActivate: mapToCanActivate([
      TokenGuard,
      ProductGuard,
      LegalDocumentGuard,
      ExistingAccountGuard,
      SetPasswordGuard,
    ]),
    children: [
      {
        path: '',
        canActivate: mapToCanActivate([PulseSurveyAvailableGuard]),
        loadChildren: () =>
          import('@kp/screen-resources/screen-resources.module').then((mod) => mod.ScreenResourcesModule),
      },
    ],
  },
  {
    path: 'help',
    loadChildren: () => import('@kp/screen-help/screen-help.module').then((mod) => mod.ScreenHelpModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'login',
    loadChildren: () => import('@kp/screen-login/screen-login.module').then((mod) => mod.ScreenLoginModule),
  },
  {
    path: 'logout',
    component: ScreenLogoutComponent,
  },
  {
    path: 'deletion',
    component: ScreenDeletionRequestedComponent,
  },
  {
    path: 'deletion-confirmation',
    component: ScreenDeletionConfirmationComponent,
  },
  {
    path: 'privacy',
    loadChildren: () => import('@kp/screen-privacy/screen-privacy.module').then((mod) => mod.ScreenPrivacyModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'privacy-pledge',
    component: ScreenPrivacyPledgeComponent,
    canActivate: mapToCanActivate([TokenGuard]),
  },
  {
    path: 'privacy-full',
    loadChildren: () =>
      import('@kp/screen-privacy-full/screen-privacy-full.module').then((mod) => mod.ScreenPrivacyFullModule),
    canActivate: mapToCanActivate([AuthenticatedGuard]),
  },
  {
    path: 'eula',
    loadChildren: () => import('@kp/screen-eula/screen-eula.module').then((mod) => mod.ScreenEulaModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'progress',
    loadChildren: () => import('@kp/screen-progress/screen-progress.module').then((mod) => mod.ScreenProgressModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard]),
  },
  {
    path: 'me',
    loadChildren: () => import('@kp/screen-me/screen-me.module').then((mod) => mod.ScreenMeModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard]),
  },
  {
    path: 'mobile_app_info',
    component: ScreenMobileAppInfoComponent,
    canActivate: mapToCanActivate([ProductGuard]),
  },
  {
    // using nested routes to ensure LegalDocumentGuard runs before PulseSurveyAvailableGuard is run
    path: 'today',
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard, ExistingAccountGuard]),
    children: [
      {
        path: '',
        canActivate: mapToCanActivate([TrafficCoordinatorGuard]),
        loadChildren: () => import('@kp/screen-today/screen-today.module').then((mod) => mod.ScreenTodayModule),
        data: {
          guards: [Guards.PROFILE_DIALOGUE_GUARD, Guards.PULSE_SURVEY_AVAILABLE_GUARD],
        },
      },
    ],
  },
  {
    path: 'purposeful-days',
    loadChildren: () =>
      import('@kp/screen-daily-challenge/screen-daily-challenge.module').then((mod) => mod.ScreenDailyChallengeModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard]),
  },
  {
    path: 'question',
    component: SingleQuestionComponent,
    canActivate: mapToCanActivate([TokenGuard]),
  },
  {
    path: 'pulse_survey',
    loadChildren: () =>
      import('@kp/screen-pulse-survey/screen-pulse-survey.module').then((mod) => mod.ScreenPulseSurveyModule),
    canActivate: mapToCanActivate([DataLinkGuard, LegalDocumentGuard]),
  },
  {
    path: 'question_set',
    loadChildren: () => import('@kp/dialogue/dialogue.module').then((mod) => mod.DialogueModule),
    canActivate: mapToCanActivate([TokenGuard]),
  },
  {
    path: 'quest',
    loadChildren: () => import('@kp/screen-quest/screen-quest.module').then((mod) => mod.ScreenQuestModule),
    canActivate: mapToCanActivate([AuthenticatedGuard]),
  },
  {
    path: 'dialogue',
    loadChildren: () => import('@kp/dialogue/dialogue.module').then((mod) => mod.DialogueModule),
    canActivate: mapToCanActivate([TokenGuard]),
  },
  {
    path: 'welcome',
    loadChildren: () => import('@kp/onboarding/onboarding.module').then((mod) => mod.OnboardingModule),
  },
  {
    path: 'reminders',
    loadChildren: () => import('@kp/screen-reminders/screen-reminders.module').then((mod) => mod.ScreenRemindersModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, ProductGuard]),
  },
  {
    path: 'report',
    loadChildren: () => import('@kp/screen-report/screen-report.module').then((mod) => mod.ScreenReportModule),
    canActivate: mapToCanActivate([TokenGuard, LegalDocumentGuard]),
  },
  {
    path: 'report/:reportId/:state',
    loadChildren: () => import('@kp/screen-report/screen-report.module').then((mod) => mod.ScreenReportModule),
    pathMatch: 'full',
    canActivate: mapToCanActivate([TokenGuard, LegalDocumentGuard]),
  },
  {
    path: 'report/:reportId',
    loadChildren: () => import('@kp/screen-report/screen-report.module').then((mod) => mod.ScreenReportModule),
    canActivate: mapToCanActivate([TokenGuard, LegalDocumentGuard]),
  },
  {
    path: 'checkout/success/reactivate/:session_id',
    redirectTo: 'today',
  },
  {
    path: 'checkout/success/onboarding/mobile/:session_id',
    pathMatch: 'full',
    redirectTo: 'welcome/mobile-app/interstitial',
  },
  {
    path: 'checkout/success/onboarding/:session_id',
    pathMatch: 'full',
    redirectTo: 'welcome/mobile-app/interstitial',
  },
  {
    path: 'checkout/reactivate/cancel',
    redirectTo: 'login',
  },
  {
    path: 'checkout/onboarding/cancel',
    redirectTo: 'welcome',
  },
  {
    path: 'thank_you',
    component: ScreenThankYouComponent,
  },
  {
    path: 'delete',
    loadChildren: () =>
      import('@kp/screen-account-deletion/screen-account-deletion.module').then(
        (mod) => mod.ScreenAccountDeletionModule
      ),
    canActivate: mapToCanActivate([DeletionGuard]),
  },
  {
    path: 'unsubscribe',
    loadChildren: () =>
      import('@kp/screen-email-subscription/screen-email-subscription.module').then(
        (mod) => mod.ScreenEmailSubscriptionModule
      ),
  },
  {
    path: 'cards/take-action',
    loadChildren: () =>
      import('@kp/screen-take-action/screen-take-action.module').then((mod) => mod.ScreenTakeActionModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'cards/actions',
    redirectTo: 'cards/take-action',
  },
  {
    path: 'cards/quests',
    redirectTo: 'cards/take-action',
  },
  {
    path: 'cards/:category',
    loadChildren: () =>
      import('@kp/screen-card-category/screen-card-category.module').then((mod) => mod.ScreenCardCategoryModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'settings',
    loadChildren: () => import('@kp/settings/screen-settings.module').then((mod) => mod.ScreenSettingsModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: 'challenges',
    loadChildren: () => import('@kp/challenges/challenges.module').then((mod) => mod.ChallengesModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard, ProductGuard, TagGuard]),
    data: {
      requiredTags: [{ tag: TagKey.social_challenges_enabled, redirectUrl: '/error' }],
    },
  },
  {
    path: 'invite',
    loadChildren: () => import('@kp/screen-invites/screen-invites.module').then((mod) => mod.ScreenInvitesModule),
    canActivate: mapToCanActivate([AuthenticatedGuard, LegalDocumentGuard]),
  },
  {
    path: ':groupCode/:anythingElse',
    pathMatch: 'full',
    redirectTo: 'welcome/error',
  },
  {
    path: ':groupCode',
    redirectTo: '/initial/:groupCode',
  },
  {
    path: '',
    pathMatch: 'full',
    // need empty children array to allow for canActivate;
    // need one of component, children or redirectTo defined per Angular spec
    children: [],
    canActivate: mapToCanActivate([DataLinkGuard, EmptyRedirectGuard]),
  },
  { path: '**', redirectTo: '/error' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
